import React, { useState } from 'react';
import {
  Box,
  Cloud,
  Github,
  Loader2,
  Save,
  Train,
  Wand2,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { CloudProvidersSection } from '../snagcart/SettingsSections';

const VISIBLE_BUILDER_SERVICES = ['github', 'railway', 'supabase', 'netlify'];

function getToken() {
  return localStorage.getItem('token') || '';
}

const BUILDER_SERVICES = {
  github: {
    icon: Github,
    title: 'GitHub',
    blurb: 'Create repos and push generated code for sharing, versioning, and deploy flows.',
    fields: [
      { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_…' },
      { key: 'username', label: 'Username', type: 'text', placeholder: 'your-username' },
    ],
  },
  railway: {
    icon: Train,
    title: 'Railway',
    blurb: 'Deploy generated apps to Railway after GitHub push.',
    fields: [
      { key: 'token', label: 'API Token', type: 'password', placeholder: 'rw_…' },
      { key: 'project_id', label: 'Project ID', type: 'text', placeholder: '' },
      { key: 'environment_id', label: 'Environment ID', type: 'text', placeholder: '' },
      { key: 'service_id', label: 'Service ID', type: 'text', placeholder: '' },
    ],
  },
  netlify: {
    icon: Cloud,
    title: 'Netlify',
    blurb: 'Optional static deploy target.',
    fields: [
      { key: 'token', label: 'API Token', type: 'password', placeholder: 'nfp_…' },
      { key: 'site_id', label: 'Site ID (optional)', type: 'text', placeholder: '' },
    ],
  },
  supabase: {
    icon: Box,
    title: 'Supabase',
    blurb: 'Wire generated apps to Supabase — client, auth helpers, and RLS migration SQL.',
    fields: [
      { key: 'url', label: 'Project URL', type: 'text', placeholder: 'https://xyz.supabase.co' },
      { key: 'anon_key', label: 'Anon Key', type: 'password', placeholder: 'eyJ…' },
      { key: 'service_key', label: 'Service Role Key (optional)', type: 'password', placeholder: 'eyJ…' },
    ],
  },
};

function CredentialCard({ serviceKey, def, stored, saving, onSave, onDisconnect }) {
  const Icon = def.icon;
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState(false);

  const handleSave = async () => {
    const patch = {};
    for (const field of def.fields) {
      if (draft[field.key] !== undefined && draft[field.key] !== '') {
        patch[field.key] = draft[field.key];
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.error('Enter at least one value to save');
      return;
    }
    const result = await onSave({ services: { [serviceKey]: patch } });
    if (result?.ok) {
      setDraft({});
      toast.success(`${def.title} saved`);
    }
  };

  const masked = (val) => (val && String(val).startsWith('••••') ? val : '');

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a]" data-testid={`app-builder-settings-${serviceKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-white/[0.02]"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
          <Icon className="h-4 w-4 text-zinc-200" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{def.title}</span>
            {stored.connected ? (
              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
                Connected
              </span>
            ) : (
              <span className="rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                Not set
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-zinc-500">{def.blurb}</p>
        </div>
      </button>

      {open ? (
        <div className="space-y-3 border-t border-white/10 px-4 pb-4 pt-3">
          {def.fields.map((field) => (
            <div key={field.key}>
              <label className="mb-1 block font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                {field.label}
              </label>
              <input
                type={field.type}
                value={draft[field.key] ?? ''}
                placeholder={masked(stored[field.key]) || field.placeholder}
                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                className="w-full rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-emerald-400/40 focus:outline-none"
                data-testid={`app-builder-settings-${serviceKey}-${field.key}`}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-1">
            {stored.connected ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDisconnect(serviceKey, def.fields)}
                className="border-red-500/30 text-red-300 hover:bg-red-500/10"
              >
                Disconnect
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
            >
              {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ToggleRow({ label, description, checked, disabled, saving, onChange, testId }) {
  return (
    <label
      className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-white/10 bg-black/40 px-4 py-3"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="mt-1 text-xs leading-5 text-zinc-500">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled || saving}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition',
          checked ? 'bg-emerald-500/80' : 'bg-zinc-700',
          (disabled || saving) && 'opacity-50',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition',
            checked ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </label>
  );
}

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AppBuilderSettingsPanel({ settingsHook }) {
  const { settings, loading, loadError, saving, save, reload } = settingsHook;
  const isLoggedIn = Boolean(getToken());

  const services = settings?.services || {};
  const core = settings?.core || {};
  const visibleServices = VISIBLE_BUILDER_SERVICES
    .map((key) => [key, BUILDER_SERVICES[key]])
    .filter(([, def]) => Boolean(def));
  const autonomousMode = core.autonomous_mode !== false;
  const fullStackMode = core.full_stack_mode !== false;
  const fastMvpMode = Boolean(core.fast_mvp_mode);
  const autoDeployEnabled = Boolean(services.auto_deploy?.enabled);

  const saveCoreToggle = async (key, value) => {
    const patch = { [key]: value };
    if (key === 'autonomous_mode' && value) {
      patch.skip_clarification = true;
    }
    const result = await save({ core: patch });
    if (result?.ok) {
      toast.success('Autonomy settings saved');
    }
  };

  const saveAutoDeploy = async (enabled) => {
    const result = await save({ services: { auto_deploy: { enabled } } });
    if (result?.ok) {
      toast.success(enabled ? 'Auto-deploy enabled' : 'Auto-deploy disabled');
    }
  };

  const disconnectService = async (serviceKey, fields) => {
    const wipe = {};
    fields.forEach((f) => { wipe[f.key] = ''; });
    const result = await save({ services: { [serviceKey]: wipe } });
    if (result?.ok) {
      toast.success('Credentials removed');
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pr-1" data-testid="app-builder-settings-panel">
      {!isLoggedIn ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          Please log in to save API keys and service credentials.
        </div>
      ) : null}
      {loadError && loadError !== 'not_authenticated' ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          <span>Could not load saved settings ({loadError}). Saves may still work — try again.</span>
          <Button type="button" size="sm" variant="outline" onClick={reload} className="shrink-0 border-red-400/30 text-red-100">
            Retry
          </Button>
        </div>
      ) : null}

      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-2">
        <CloudProvidersSection settings={settings} save={save} />
      </div>

      <div className="rounded-2xl border border-violet-500/20 bg-violet-500/[0.04] p-5" data-testid="app-builder-autonomous-settings">
        <div className="mb-4 flex items-center gap-2">
          <Zap className="h-4 w-4 text-violet-300" />
          <div>
            <h4 className="text-sm font-semibold text-white">Autonomous mode</h4>
            <p className="text-xs text-zinc-500">Emergent-style: build, verify in E2B, and optionally deploy without manual steps.</p>
          </div>
        </div>
        <div className="space-y-3">
          <ToggleRow
            label="Autonomous builder"
            description="Skip clarification questions, auto-apply changes, and run full QA loops until the build passes."
            checked={autonomousMode}
            saving={saving}
            disabled={!isLoggedIn}
            onChange={(v) => saveCoreToggle('autonomous_mode', v)}
            testId="app-builder-settings-autonomous-mode"
          />
          <ToggleRow
            label="Full-stack by default"
            description="New builds include a FastAPI backend, database, and wired REST API — not landing-page-only."
            checked={fullStackMode}
            saving={saving}
            disabled={!isLoggedIn}
            onChange={(v) => saveCoreToggle('full_stack_mode', v)}
            testId="app-builder-settings-full-stack-mode"
          />
          <ToggleRow
            label="Fast MVP mode"
            description="Generate a streamlined landing page in under 10 seconds. Skips design review and QA loops for speed."
            checked={fastMvpMode}
            saving={saving}
            disabled={!isLoggedIn}
            onChange={(v) => saveCoreToggle('fast_mvp_mode', v)}
            testId="app-builder-settings-fast-mvp-mode"
          />
          <ToggleRow
            label="Auto-deploy after verification"
            description="Push to GitHub and deploy when the build passes QA. Requires GitHub credentials below."
            checked={autoDeployEnabled}
            saving={saving}
            disabled={!isLoggedIn}
            onChange={saveAutoDeploy}
            testId="app-builder-settings-auto-deploy"
          />
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-violet-300" />
          <h4 className="text-sm font-semibold text-white">Deploy services</h4>
        </div>
        <div className="grid gap-3">
          {visibleServices.map(([key, def]) => (
            <CredentialCard
              key={key}
              serviceKey={key}
              def={def}
              stored={services[key] || {}}
              saving={saving}
              onSave={save}
              onDisconnect={disconnectService}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/40 p-4 text-xs leading-6 text-zinc-500">
        <p className="font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-[0.18em] text-zinc-400">Quick setup order</p>
        <ol className="mt-2 list-decimal space-y-1 pl-4">
          <li>Save <strong className="text-zinc-300">OpenAI / Anthropic / Google</strong> keys under Cloud Providers</li>
          <li>Pick your default model in the same section (or use the header model dropdown when building)</li>
          <li>Enable <strong className="text-zinc-300">Autonomous mode</strong> + <strong className="text-zinc-300">Full-stack</strong> above</li>
          <li>Save <strong className="text-zinc-300">GitHub</strong> token + username (required for auto-deploy)</li>
          <li>Build a project — E2B verifies in an isolated sandbox when <code className="text-zinc-400">E2B_API_KEY</code> is set</li>
          <li>Optional: save <strong className="text-zinc-300">Railway</strong> or <strong className="text-zinc-300">Netlify</strong> for deployment targets</li>
          <li>Optional: save <strong className="text-zinc-300">Supabase</strong> URL + anon key for database-backed apps</li>
        </ol>
      </div>
    </div>
  );
}
